import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Select, FormControl, InputLabel } from "@mui/material";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
  Button,
  Avatar,
  alpha,
  Skeleton,
  useTheme,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableChartIcon from "@mui/icons-material/TableChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import { fetchTimeline } from "../../service/web_service";
import {
  generateTimelinePDF,
  generateSimplifiedPDF,
  generateExcelReport,
  generateChartOnlyPDF,
} from "../../utils/timelineExportUtils";

const DEFAULT_MIN_NORMAL_TEMP = 38;
const DEFAULT_MAX_NORMAL_TEMP = 42;

const Y_AXIS_TICKS = [
  30, 30.5, 31, 31.5, 32, 32.5, 33, 33.5, 34, 34.5, 35, 35.5, 36, 36.5, 37,
  37.5, 38, 38.5, 39, 39.5, 40, 40.5, 41, 41.5, 42, 42.5, 43, 43.5, 44, 44.5,
  45, 45.5, 46, 46.5, 47, 47.5, 48, 48.5, 49, 49.5, 50,
];

const AnimatedStatCard = ({ title, value, icon, color, gradient, loading }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 3,
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 6,
      },
      position: "relative",
      background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.02)} 100%)`,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: gradient,
      }}
    />
    <CardContent 
      sx={{ 
        p: 2,
        pb: "1 !important",
        "&:last-child": {
          pb: 1
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 0.5, fontSize: "0.65rem" }}
          >
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width="80%" height={30} />
          ) : (
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: color, fontSize: "1.5rem", lineHeight: 1.2 }}
            >
              {value || 0}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: alpha(color, 0.15), width: 32, height: 32 }}>
          {React.cloneElement(icon, { sx: { fontSize: 18, color: color } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, minNormal, maxNormal }) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;

    const dateObj = new Date(dataPoint.fullDateTime);
    const formattedDate = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const formattedTime = dateObj.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const weekday = dateObj.toLocaleDateString("en-GB", { weekday: "long" });

    const temperature = dataPoint.temperature;
    const isLow = temperature < minNormal;
    const isHigh = temperature > maxNormal;

    let statusText = "✓ Normal";
    let statusColor = "#4caf50";
    if (isLow) {
      statusText = `⚠️ Below Normal (< ${minNormal}°C)`;
      statusColor = "#f44336";
    } else if (isHigh) {
      statusText = `⚠️ Above Normal (> ${maxNormal}°C)`;
      statusColor = "#ff9800";
    }

    return (
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          boxShadow: 4,
          minWidth: 260,
          maxWidth: 320,
          border: `2px solid ${statusColor}`,
        }}
      >
        <Box sx={{ borderBottom: `2px solid ${statusColor}`, mb: 1.5, pb: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Temperature Details
          </Typography>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Date:
            </Typography>
            <Typography variant="caption" fontWeight="500">
              {formattedDate}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              ({weekday})
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Time:
            </Typography>
            <Typography variant="caption" fontWeight="500">
              {formattedTime}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: alpha(statusColor, 0.08),
            p: 1.5,
            borderRadius: 2,
            mb: 1.5,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Temperature:
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ color: statusColor }}
          >
            {temperature.toFixed(2)}°C
          </Typography>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Chip
            label={statusText}
            size="small"
            sx={{
              bgcolor: alpha(statusColor, 0.15),
              color: statusColor,
              fontWeight: 600,
              border: `1px solid ${statusColor}`,
            }}
          />
        </Box>

        {(isLow || isHigh) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 1,
              fontStyle: "italic",
            }}
          >
            ⚠️ This reading is outside the normal range
          </Typography>
        )}
      </Paper>
    );
  }
  return null;
};

const TimeLineReport = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [showAllPoints, setShowAllPoints] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingChartPDF, setIsGeneratingChartPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [warehouseName, setWarehouseName] = useState("");

  const [minNormalTemp, setMinNormalTemp] = useState(DEFAULT_MIN_NORMAL_TEMP);
  const [maxNormalTemp, setMaxNormalTemp] = useState(DEFAULT_MAX_NORMAL_TEMP);
  const [isEditingRange, setIsEditingRange] = useState(false);

  const [tempMinValue, setTempMinValue] = useState(DEFAULT_MIN_NORMAL_TEMP);
  const [tempMaxValue, setTempMaxValue] = useState(DEFAULT_MAX_NORMAL_TEMP);

  const [filterStatus, setFilterStatus] = useState("all");

  const reportContentRef = useRef(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [filterStatus]);

  const getUrlParams = useCallback(() => {
    try {
      const fullUrl = window.location.href;
      console.log("Full URL:", fullUrl);

      const hash = window.location.hash;
      let queryString = "";

      if (hash.includes("?")) {
        queryString = hash.split("?")[1];
      }

      console.log("Query String:", queryString);

      if (!queryString) {
        console.warn("No query string found in URL");
        return {};
      }

      let fixedQueryString = queryString;

      fixedQueryString = fixedQueryString.replace(
        /([?&])orgId([A-Z0-9]+)/gi,
        "$1orgId=$2"
      );

      fixedQueryString = fixedQueryString.replace(
        /([?&])warehouseId([A-Z0-9]+)/gi,
        "$1warehouseId=$2"
      );

      fixedQueryString = fixedQueryString.replace(
        /warehouseId([A-Z0-9]+)warehouse=([^&]*)/gi,
        "warehouseId=$1&warehouse=$2"
      );

      if (fixedQueryString.startsWith("orgId")) {
        fixedQueryString = fixedQueryString.replace(/^orgId([A-Z0-9]+)/, "orgId=$1");
      }
      if (fixedQueryString.startsWith("warehouseId")) {
        fixedQueryString = fixedQueryString.replace(/^warehouseId([A-Z0-9]+)/, "warehouseId=$1");
      }

      console.log("🔧 Fixed Query String:", fixedQueryString);

      const params = new URLSearchParams(fixedQueryString);
      console.log("All URL parameters after fix:", Array.from(params.keys()));

      const getParam = (key) => {
        const value = params.get(key);
        if (!value) return "";
        try {
          return decodeURIComponent(value);
        } catch (e) {
          return value;
        }
      };

      const extractedParams = {
        fromDate: getParam("fromDate"),
        toDate: getParam("toDate"),
        organization: getParam("organization"),
        season: getParam("season"),
        typeOfDryer: getParam("typeOfDryer"),
        binNo: getParam("binNo"),
        cycleNo: getParam("cycleNo"),
        orgId: getParam("orgId"),
        warehouseId: getParam("warehouseId"),
        warehouseName: getParam("warehouse"),
        basicauth: getParam("basicauth")
      };

      console.log("✅ Extracted Params:", {
        ...extractedParams,
      });
      console.log("🏭 Warehouse ID:", extractedParams.warehouseId);
      console.log("🏢 Organization ID:", extractedParams.orgId);
      console.log("📦 Warehouse Name from URL:", extractedParams.warehouseName);
      console.log("🔐 Has Basicauth:", !!extractedParams.basicauth);

      return extractedParams;
    } catch (error) {
      console.error("Error parsing URL:", error);
      return {};
    }
  }, []);

  const urlParams = getUrlParams();

  useEffect(() => {
    console.log("Setting warehouse name from URL params...");
    console.log("urlParams.warehouseName:", urlParams.warehouseName);
    console.log("urlParams.warehouseId:", urlParams.warehouseId);

    if (urlParams.warehouseName && urlParams.warehouseName.trim() !== "") {
      setWarehouseName(urlParams.warehouseName);
      console.log("✅ Warehouse name set from URL:", urlParams.warehouseName);
    } else {
      console.warn("No warehouse name found in URL");
      setWarehouseName("");
    }
  }, [urlParams.warehouseName, urlParams.warehouseId]);

  const fetchDataDirectly = useCallback(async () => {
    const params = getUrlParams();

    console.log("Fetching with params:", {
      ...params,
      basicauth: params.basicauth ? "***" : ""
    });

    if (!params.fromDate || !params.toDate) {
      console.error("Missing required dates in URL", params);
      setLocalError(
        "Missing fromDate or toDate in URL. Please check the URL format.",
      );
      return;
    }

    if (!params.basicauth) {
      console.error("Missing basicauth in URL", {
        basicauth: params.basicauth ? "present" : "missing"
      });
      setLocalError(
        "Missing basicauth in URL. Please check the URL format.",
      );
      return;
    }

    setLocalLoading(true);
    setLocalError(null);

    try {
      let finalOrgId = params.orgId;
      let finalWarehouseId = params.warehouseId;

      if (finalOrgId && finalWarehouseId && finalOrgId === finalWarehouseId) {
        console.warn(
          "⚠️ orgId and warehouseId are identical. Using only orgId for filtering.",
        );
        finalWarehouseId = "";
      }

      if (!finalOrgId && finalWarehouseId) {
        console.log(
          "🔄 orgId is empty, using warehouseId as organization filter:",
          finalWarehouseId,
        );
        finalOrgId = finalWarehouseId;
        finalWarehouseId = "";
      }

      console.log("🚀 Calling fetchTimeline with basicauth:", {
        fromDate: params.fromDate,
        toDate: params.toDate,
        organization: params.organization,
        season: params.season,
        typeOfDryer: params.typeOfDryer,
        binNo: params.binNo,
        cycleNo: params.cycleNo,
        orgId: finalOrgId,
        warehouseId: finalWarehouseId,
        Basicauth: !!params.basicauth
      });

      const result = await fetchTimeline(
        params.fromDate,
        params.toDate,
        params.organization,
        params.season,
        params.typeOfDryer,
        params.binNo,
        params.cycleNo,
        finalOrgId,
        finalWarehouseId,
        params.basicauth  
      );

      setLocalData(result || []);
      console.log(`✅ Successfully fetched ${result?.length || 0} records`);

      if (result?.length === 0) {
        setLocalError("No data found for the given criteria");
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setLocalError(`Failed to fetch data: ${err.message}`);
      setLocalData([]);
    } finally {
      setLocalLoading(false);
    }
  }, [getUrlParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDataDirectly();
    }, 100);

    return () => clearTimeout(timer);
  }, [fetchDataDirectly]);

  const sortedData = useMemo(() => {
    if (!localData || localData.length === 0) return [];

    return [...localData].sort((a, b) => {
      const dateA = new Date(a.datetime);
      const dateB = new Date(b.datetime);
      return dateA - dateB;
    });
  }, [localData]);

  const getTemperatureStyle = (temperature) => {
    const temp = parseFloat(temperature);
    if (temp < minNormalTemp) {
      return {
        backgroundColor: "#ffebee",
        color: "#c62828",
        fontWeight: "bold",
        borderLeft: "4px solid #f44336",
      };
    } else if (temp > maxNormalTemp) {
      return {
        backgroundColor: "#fff3e0",
        color: "#e65100",
        fontWeight: "bold",
        borderLeft: "4px solid #ff9800",
      };
    }
    return {};
  };

  const chartData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) {
      return [];
    }

    return sortedData.map((item, index) => {
      const dateObj = new Date(item.datetime);
      const dateTimeStr = dateObj.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      const temperature = parseFloat(item.upfronttemp) || 0;

      return {
        datetime: dateTimeStr,
        temperature: temperature,
        fullDateTime: item.datetime,
        isAbnormal: temperature < minNormalTemp || temperature > maxNormalTemp,
      };
    });
  }, [sortedData, minNormalTemp, maxNormalTemp]);

  const statistics = useMemo(() => {
    if (!sortedData || sortedData.length === 0) {
      return {
        totalRecords: 0,
        avgTemperature: 0,
        maxTemperature: 0,
        minTemperature: 0,
        abnormalCount: 0,
        lowCount: 0,
        highCount: 0,
      };
    }

    const totalRecords = sortedData.length;
    const temperatures = sortedData.map(
      (item) => parseFloat(item.upfronttemp) || 0,
    );
    const avgTemperature = (
      temperatures.reduce((sum, temp) => sum + temp, 0) / totalRecords
    ).toFixed(2);
    const maxTemperature = Math.max(...temperatures);
    const minTemperature = Math.min(...temperatures);

    const lowCount = temperatures.filter((temp) => temp < minNormalTemp).length;
    const highCount = temperatures.filter(
      (temp) => temp > maxNormalTemp,
    ).length;

    return {
      totalRecords,
      avgTemperature,
      maxTemperature,
      minTemperature,
      abnormalCount: lowCount + highCount,
      lowCount,
      highCount,
    };
  }, [sortedData, minNormalTemp, maxNormalTemp]);

  const getFilteredData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return [];

    if (filterStatus === "all") return sortedData;

    return sortedData.filter((item) => {
      const temp = parseFloat(item.upfronttemp);
      const isLow = temp < minNormalTemp;
      const isHigh = temp > maxNormalTemp;

      if (filterStatus === "normal") return !isLow && !isHigh;
      if (filterStatus === "below") return isLow;
      if (filterStatus === "above") return isHigh;
      return true;
    });
  }, [sortedData, filterStatus, minNormalTemp, maxNormalTemp]);

  const getXAxisInterval = () => {
    const recordCount = chartData.length;
    if (recordCount <= 20) return 0;
    if (recordCount <= 50) return 2;
    if (recordCount <= 100) return 4;
    if (recordCount <= 200) return 9;
    return Math.floor(recordCount / 20);
  };

  const handleEditClick = () => {
    setTempMinValue(minNormalTemp);
    setTempMaxValue(maxNormalTemp);
    setIsEditingRange(true);
  };

  const handleMinTempChange = (event) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setTempMinValue(value);
    } else if (event.target.value === "") {
      setTempMinValue("");
    }
  };

  const handleMaxTempChange = (event) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setTempMaxValue(value);
    } else if (event.target.value === "") {
      setTempMaxValue("");
    }
  };

  const handleApplyRange = () => {
    let newMin = tempMinValue;
    let newMax = tempMaxValue;

    if (newMin === "" || isNaN(newMin)) {
      alert("Please enter a valid minimum temperature");
      return;
    }

    if (newMax === "" || isNaN(newMax)) {
      alert("Please enter a valid maximum temperature");
      return;
    }

    if (newMin < 0) {
      alert(`Minimum range cannot be less than 0. Please enter a value >= 0.`);
      return;
    }
    if (newMax > 100) {
      alert(
        `Maximum range cannot be greater than 100. Please enter a value <= 100.`,
      );
      return;
    }

    if (newMin >= newMax) {
      alert(
        `Invalid range: Minimum (${newMin}°C) must be less than Maximum (${newMax}°C). Please enter valid values.`,
      );
      return;
    }

    setMinNormalTemp(newMin);
    setMaxNormalTemp(newMax);
    setIsEditingRange(false);
  };

  const handleResetRange = () => {
    setMinNormalTemp(DEFAULT_MIN_NORMAL_TEMP);
    setMaxNormalTemp(DEFAULT_MAX_NORMAL_TEMP);
    setTempMinValue(DEFAULT_MIN_NORMAL_TEMP);
    setTempMaxValue(DEFAULT_MAX_NORMAL_TEMP);
    setIsEditingRange(false);
  };

  const handleCancelEdit = () => {
    setIsEditingRange(false);
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportFullPDF = async () => {
    if (isGeneratingPDF) return;
    handleExportMenuClose();
    setIsGeneratingPDF(true);

    try {
      await generateTimelinePDF("report-content", {
        filename: `Temperature_Report_${urlParams.fromDate}_to_${urlParams.toDate}.pdf`,
        title: `Temperature Timeline Report\n${urlParams.fromDate} to ${urlParams.toDate}`,
        orientation: "landscape",
        organization: urlParams.organization,
        season: urlParams.season,
        typeOfDryer: urlParams.typeOfDryer,
        binNo: urlParams.binNo,
        cycleNo: urlParams.cycleNo,
        warehouseName: warehouseName,
        minNormalTemp: minNormalTemp,
        maxNormalTemp: maxNormalTemp,
      });

      alert("✅ Full PDF report generated successfully!");
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportChartPDF = async () => {
    if (isGeneratingChartPDF) return;
    handleExportMenuClose();

    setIsGeneratingChartPDF(true);
    try {
      const chartContainer = document
        .querySelector(".recharts-responsive-container")
        ?.closest(".MuiPaper-root");

      if (!chartContainer) {
        alert("❌ Chart not found. Please make sure chart data is loaded.");
        setIsGeneratingChartPDF(false);
        return;
      }

      await generateChartOnlyPDF(chartContainer, {
        filename: `Temperature_Chart_${urlParams.fromDate}_to_${urlParams.toDate}.pdf`,
        title: "Temperature Trend Chart",
        fromDate: urlParams.fromDate,
        toDate: urlParams.toDate,
        organization: urlParams.organization,
        season: urlParams.season,
        typeOfDryer: urlParams.typeOfDryer,
        binNo: urlParams.binNo,
        cycleNo: urlParams.cycleNo,
        warehouseName: warehouseName,
        minNormalTemp: minNormalTemp,
        maxNormalTemp: maxNormalTemp,
      });

      alert("✅ Chart PDF generated successfully!");
    } catch (error) {
      console.error("Chart PDF generation failed:", error);
      alert("❌ Failed to generate Chart PDF: " + error.message);
    } finally {
      setIsGeneratingChartPDF(false);
    }
  };

  const handleExportDataPDF = async () => {
    if (isGeneratingPDF) return;
    handleExportMenuClose();

    setIsGeneratingPDF(true);
    try {
      await generateSimplifiedPDF(sortedData, {
        filename: `Temperature_Data_${urlParams.fromDate}_to_${urlParams.toDate}.pdf`,
        title: "Temperature Data Report",
        fromDate: urlParams.fromDate,
        toDate: urlParams.toDate,
        statistics: statistics,
        warehouseName: warehouseName,
        organization: urlParams.organization,
        season: urlParams.season,
        typeOfDryer: urlParams.typeOfDryer,
        binNo: urlParams.binNo,
        cycleNo: urlParams.cycleNo,
        minNormalTemp: minNormalTemp,
        maxNormalTemp: maxNormalTemp,
      });
      alert("✅ Data PDF generated successfully!");
    } catch (error) {
      console.error("Data PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    if (isGeneratingExcel) return;
    handleExportMenuClose();

    setIsGeneratingExcel(true);
    try {
      await generateExcelReport(sortedData, statistics, {
        filename: `Temperature_Report_${urlParams.fromDate}_to_${urlParams.toDate}.xlsx`,
        fromDate: urlParams.fromDate,
        toDate: urlParams.toDate,
        chartData: chartData,
        warehouseName: warehouseName,
        organization: urlParams.organization,
        season: urlParams.season,
        typeOfDryer: urlParams.typeOfDryer,
        binNo: urlParams.binNo,
        cycleNo: urlParams.cycleNo,
        minNormalTemp: minNormalTemp,
        maxNormalTemp: maxNormalTemp,
      });
      alert("✅ Excel report generated successfully!");
    } catch (error) {
      console.error("Excel generation failed:", error);
      alert("Failed to generate Excel report: " + error.message);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  if (localLoading && sortedData.length === 0) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Timeline Report...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          background: "linear-gradient(to right, #b8d5f53f, #ddeaf5)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 25,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Temperature Timeline Report
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Tooltip title="Export Options">
                <span>
                  <IconButton
                    onClick={handleExportMenuOpen}
                    disabled={
                      localLoading ||
                      sortedData.length === 0 ||
                      isGeneratingPDF ||
                      isGeneratingChartPDF ||
                      isGeneratingExcel
                    }
                    color="primary"
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportFullPDF} disabled={isGeneratingPDF}>
                  <PictureAsPdfIcon
                    sx={{ mr: 1, fontSize: 20, color: "#f44336" }}
                  />
                  Export Full Report (PDF)
                </MenuItem>

                <MenuItem
                  onClick={handleExportChartPDF}
                  disabled={isGeneratingChartPDF}
                >
                  <PictureAsPdfIcon
                    sx={{ mr: 1, fontSize: 20, color: "#4caf50" }}
                  />
                  Export Chart Only (PDF)
                </MenuItem>

                <MenuItem onClick={handleExportExcel} disabled={isGeneratingExcel}>
                  <TableChartIcon
                    sx={{ mr: 1, fontSize: 20, color: "#2196f3" }}
                  />
                  Export to Excel (XLSX)
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {urlParams.organization && (
              <Chip
                label={
                  <span style={{ fontSize: "14px" }}>
                    Organization:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {urlParams.organization}
                    </strong>
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  "& .MuiChip-label": {
                    padding: "4px 12px",
                  },
                }}
              />
            )}

            {warehouseName && (
              <Chip
                label={
                  <span style={{ fontSize: "14px" }}>
                    Warehouse:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {warehouseName}
                    </strong>
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  "& .MuiChip-label": {
                    padding: "4px 12px",
                  },
                }}
              />
            )}

            {urlParams.season && (
              <Chip
                label={
                  <span style={{ fontSize: "14px" }}>
                    Season:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {urlParams.season}
                    </strong>
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  "& .MuiChip-label": {
                    padding: "4px 12px",
                  },
                }}
              />
            )}

            {urlParams.typeOfDryer && (
              <Chip
                label={
                  <span style={{ fontSize: "14px" }}>
                    Dryer Type:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {urlParams.typeOfDryer}
                    </strong>
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  "& .MuiChip-label": {
                    padding: "4px 12px",
                  },
                }}
              />
            )}

            {urlParams.binNo && (
              <Chip
                label={
                  <span style={{ fontSize: "14px" }}>
                    Bin No:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {urlParams.binNo}
                    </strong>
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  "& .MuiChip-label": {
                    padding: "4px 12px",
                  },
                }}
              />
            )}

            {urlParams.cycleNo && (
              <Chip
                label={
                  <span style={{ fontSize: "14px" }}>
                    Cycle No:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {urlParams.cycleNo}
                    </strong>
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  "& .MuiChip-label": {
                    padding: "4px 12px",
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <div id="report-content" ref={reportContentRef}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.04)} 0%, ${alpha(theme.palette.success.light, 0.08)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
          }}
        >
          {isEditingRange ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: theme.palette.success.main,
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                    }}
                  >
                    <ShowChartIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Normal Temperature Range (Green Zone)
                  </Typography>
                </Box>

                <TextField
                  label="Min Normal (°C)"
                  type="number"
                  value={tempMinValue}
                  onChange={handleMinTempChange}
                  size="small"
                  sx={{ width: 130 }}
                  inputProps={{
                    step: 0.5,
                    min: 0,
                    max: 100,
                  }}
                />

                <Typography fontWeight={500}>to</Typography>

                <TextField
                  label="Max Normal (°C)"
                  type="number"
                  value={tempMaxValue}
                  onChange={handleMaxTempChange}
                  size="small"
                  sx={{ width: 130 }}
                  inputProps={{
                    step: 0.5,
                    min: 0,
                    max: 100,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyRange}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                >
                  Apply
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelEdit}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetRange}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                >
                  Reset to Default
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: theme.palette.success.main,
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Normal Temperature Range (Green Zone)
                  </Typography>

                  <Chip
                    label={`${minNormalTemp}°C – ${maxNormalTemp}°C`}
                    sx={{
                      bgcolor: theme.palette.success.main,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "15px",
                      px: 1,
                      borderRadius: 2,
                    }}
                  />
                </Box>

                <Box display="flex" gap={1.5}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleEditClick}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: "#fff",
                      },
                    }}
                    startIcon={<ThermostatIcon />}
                  >
                    Edit Range
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleResetRange}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      "&:hover": {
                        color: "#fff",
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2, pb: 0 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <AnimatedStatCard
              title="Total Records"
              value={statistics.totalRecords.toLocaleString()}
              icon={<TableChartIcon />}
              color="#1976d2"
              gradient="linear-gradient(90deg, #1976d2, #42a5f5)"
              loading={localLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <AnimatedStatCard
              title="Average Temperature"
              value={`${statistics.avgTemperature}°C`}
              icon={<ShowChartIcon />}
              color="#2e7d32"
              gradient="linear-gradient(90deg, #2e7d32, #66bb6a)"
              loading={localLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <AnimatedStatCard
              title="Maximum Temperature"
              value={`${statistics.maxTemperature}°C`}
              icon={<TrendingUpIcon />}
              color={
                statistics.maxTemperature > maxNormalTemp
                  ? "#ed6c02"
                  : "#2e7d32"
              }
              gradient={
                statistics.maxTemperature > maxNormalTemp
                  ? "linear-gradient(90deg, #ed6c02, #ffa726)"
                  : "linear-gradient(90deg, #2e7d32, #66bb6a)"
              }
              loading={localLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <AnimatedStatCard
              title="Minimum Temperature"
              value={`${statistics.minTemperature}°C`}
              icon={<TrendingDownIcon />}
              color={
                statistics.minTemperature < minNormalTemp
                  ? "#d32f2f"
                  : "#2e7d32"
              }
              gradient={
                statistics.minTemperature < minNormalTemp
                  ? "linear-gradient(90deg, #d32f2f, #ef5350)"
                  : "linear-gradient(90deg, #2e7d32, #66bb6a)"
              }
              loading={localLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <AnimatedStatCard
              title="Abnormal Readings"
              value={statistics.abnormalCount}
              icon={<WarningIcon />}
              color={statistics.abnormalCount > 0 ? "#ed6c02" : "#2e7d32"}
              gradient={
                statistics.abnormalCount > 0
                  ? "linear-gradient(90deg, #ed6c02, #ffa726)"
                  : "linear-gradient(90deg, #2e7d32, #66bb6a)"
              }
              loading={localLoading}
            />
          </Grid>
        </Grid>

        {localError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        )}

        {chartData.length > 0 ? (
          <Paper
            sx={{
              p: 2,
              pb: 0,
              mb: 1,
              borderRadius: "15px",
              borderTop: "3px solid #0e7dd2",
            }}
            id="chart-only-paper"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                style={{ fontWeight: "600" }}
              >
                Temperature Trend ({chartData.length.toLocaleString()} Records)
              </Typography>

              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textAlign: "right", fontSize: "12" }}
              >
                Normal Range:{" "}
                <strong style={{ color: "#4caf50" }}>
                  {minNormalTemp}°C – {maxNormalTemp}°C (Green zone)
                </strong>{" "}
                - Readings below{" "}
                <strong style={{ color: "#f44336" }}>{minNormalTemp}°C</strong>{" "}
                are LOW, above{" "}
                <strong style={{ color: "#ff9800" }}>{maxNormalTemp}°C</strong>{" "}
                are HIGH
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={chartData}
                margin={{ top: 2, right: 30, left: 30, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="datetime"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 10, fill: "#666" }}
                  interval={getXAxisInterval()}
                  tickFormatter={(value) =>
                    value.length > 20 ? value.substring(0, 17) + "..." : value
                  }
                />
                <YAxis
                  domain={[30, 50]}
                  ticks={Y_AXIS_TICKS}
                  tick={{ fontSize: 10, fill: "#666" }}
                  tickFormatter={(value) => value.toFixed(1)}
                  label={{
                    value: "Temperature (°C)",
                    angle: -90,
                    position: "insideLeft",
                    style: {
                      fontSize: "12px",
                      fontWeight: "bold",
                      fill: "#333",
                    },
                    dy: 20,
                  }}
                  width={50}
                />

                <ReferenceArea
                  y1={minNormalTemp}
                  y2={maxNormalTemp}
                  fill="#4caf50"
                  fillOpacity={0.15}
                  stroke="#4caf50"
                  strokeWidth={2}
                />
                <ReferenceArea
                  y1={-Infinity}
                  y2={minNormalTemp}
                  fill="#f44336"
                  fillOpacity={0.1}
                />
                <ReferenceArea
                  y1={maxNormalTemp}
                  y2={Infinity}
                  fill="#ff9800"
                  fillOpacity={0.1}
                />
                <RechartsTooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      minNormal={minNormalTemp}
                      maxNormal={maxNormalTemp}
                    />
                  )}
                />
                <Legend
                  verticalAlign="top"
                  height={20}
                  formatter={(value) => (
                    <span style={{ fontSize: "11px", fontWeight: "bold" }}>
                      Temperature Trend
                    </span>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#2196f3"
                  strokeWidth={2.5}
                  dot={{ r: 2, strokeWidth: 1 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                  name="Temperature"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        ) : (
          !localLoading &&
          !localError && (
            <Paper sx={{ p: 3, mb: 2, textAlign: "center" }}>
              <Typography color="textSecondary">
                No temperature data available
              </Typography>
            </Paper>
          )
        )}

        {sortedData.length > 0 && (
          <Paper
            sx={{ p: 2, borderRadius: "15px", borderTop: "3px solid #0e7dd2" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                mb: 1,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Detailed Data ({getFilteredData.length.toLocaleString()}{" "}
                  Records)
                </Typography>
              </Box>

              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  textAlign: "right",
                  fontSize: "12px",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 110, mx: 2 }}>
                  <InputLabel id="filter-status-label">Filter</InputLabel>
                  <Select
                    labelId="filter-status-label"
                    id="filter-status-select"
                    value={filterStatus}
                    label="Filter"
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      fontSize: "0.85rem",
                      textAlign: "left",
                      "& .MuiSelect-select": { py: 0.5, px: 1.25 },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="normal"> Normal</MenuItem>
                    <MenuItem value="below">⬇ Below {minNormalTemp}°C</MenuItem>
                    <MenuItem value="above">⬆ Above {maxNormalTemp}°C</MenuItem>
                  </Select>
                </FormControl>
                Normal Range:{" "}
                <strong>
                  {minNormalTemp}°C – {maxNormalTemp}°C
                </strong>{" "}
                | Below{" "}
                <strong style={{ color: "#f44336" }}>{minNormalTemp}°C</strong>{" "}
                = Low | Above{" "}
                <strong style={{ color: "#ff9800" }}>{maxNormalTemp}°C</strong>{" "}
                = High
              </Typography>
            </Box>

            <TableContainer 
              ref={tableContainerRef} 
              sx={{ maxHeight: 300 }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  "& .MuiTableCell-head": {
                    backgroundColor: "#0e7dd2",
                    color: "#fff",
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell align="right">Temperature (°C)</TableCell>
                    <TableCell>
                      Status (vs {minNormalTemp}-{maxNormalTemp}°C)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredData.map((item, index) => {
                    const temp = parseFloat(item.upfronttemp);
                    const isLow = temp < minNormalTemp;
                    const isHigh = temp > maxNormalTemp;

                    let statusLabel = " Normal";
                    let statusColor = "success";
                    let statusVariant = "outlined";

                    if (isLow) {
                      statusLabel = `⚠️ Below ${minNormalTemp}°C`;
                      statusColor = "error";
                      statusVariant = "filled";
                    } else if (isHigh) {
                      statusLabel = `⚠️ Above ${maxNormalTemp}°C`;
                      statusColor = "warning";
                      statusVariant = "filled";
                    }

                    return (
                      <TableRow key={index} sx={getTemperatureStyle(temp)}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {new Date(item.datetime).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: isLow || isHigh ? "bold" : "normal",
                            color: isLow
                              ? "#c62828"
                              : isHigh
                                ? "#e65100"
                                : "inherit",
                          }}
                        >
                          {temp.toFixed(2)}°C
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel}
                            size="small"
                            color={statusColor}
                            variant={statusVariant}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {getFilteredData.length === 0 && (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography color="textSecondary">
                  No{" "}
                  {filterStatus === "normal"
                    ? "normal"
                    : filterStatus === "below"
                      ? "below normal"
                      : "above normal"}{" "}
                  records found
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </div>
    </Container>
  );
};

export default TimeLineReport;